import React, { ReactElement } from 'react';

import useRouteState from '@OnArk/utils/useRouteState';
import { SectionPageProps } from 'pages/[church.url]/[course.url]/[sectionId]';
import { BranchSectionPageProps } from 'pages/admin/classes/[courseId]/[sectionId]/Branch';
import { CheckInSectionPageProps } from 'pages/admin/classes/[courseId]/[sectionId]/Checkin';
import { CompletionSectionPageProps } from 'pages/admin/classes/[courseId]/[sectionId]/Completion';
import { FormSectionPageProps } from 'pages/admin/classes/[courseId]/[sectionId]/Form';
import { TestSectionPageProps } from 'pages/admin/classes/[courseId]/[sectionId]/Test';
import { TextSectionPageProps } from 'pages/admin/classes/[courseId]/[sectionId]/Text';
import { VideoSectionPageProps } from 'pages/admin/classes/[courseId]/[sectionId]/Video';
import { AutomationsPageProps } from 'pages/admin/classes/[courseId]/automations';

export type PageProps = (
  | TextSectionPageProps
  | BranchSectionPageProps
  | CheckInSectionPageProps
  | CompletionSectionPageProps
  | FormSectionPageProps
  | TestSectionPageProps
  | VideoSectionPageProps
  | Omit<SectionPageProps, 'preview'>
  | AutomationsPageProps
) & {
  teams?: SectionPageProps['teams'];
  results?: SectionPageProps['results'];
  teamsChosenElsewhere?: string[];
  types?: TestSectionPageProps['types'];
};

type SectionContextValueType = PageProps & {
  setSettings: (settings: PageProps['currentSection']['settings']) => void;
  changes: number;
  setChanges: React.Dispatch<React.SetStateAction<number>>;
  setSections: React.Dispatch<React.SetStateAction<PageProps['sections']>>;
  setCurrentSection: (changed: Partial<PageProps['currentSection']>) => void;
  setTeams: React.Dispatch<React.SetStateAction<PageProps['teams']>>;
};

const SectionContext = React.createContext<SectionContextValueType>(
  {} as SectionContextValueType
);

export const useSection = (): SectionContextValueType =>
  React.useContext(SectionContext);

export const SectionContextProvider = function SectionContextProvider({
  children,
  ...props
}: React.PropsWithChildren<PageProps>): ReactElement {
  const [changes, setChanges] = useRouteState(props.course.changed.length);
  const [sections, setSections] = useRouteState(props.sections);
  const [teams, setTeams] = useRouteState(props.teams);
  const [currentSection, updateSection] = useRouteState(props.currentSection);

  const setSettings = (settings: PageProps['currentSection']['settings']) => {
    updateSection(c => ({
      ...c,
      settings: {
        ...(c.settings as Record<string, unknown> | null),
        ...(settings as Record<string, unknown> | null),
      },
    }));
  };
  const setCurrentSection = (changed: Partial<PageProps['currentSection']>) => {
    updateSection(s => ({ ...s, ...changed }));
    setSections(sections => {
      return sections.map(s => {
        if (s.id === currentSection.id) {
          return { ...s, ...changed };
        }
        s.childSections = s.childSections.map(c =>
          c.id === currentSection.id ? { ...c, ...changed } : c
        );

        return s;
      });
    });
  };

  return (
    <SectionContext.Provider
      value={{
        ...props,
        changes,
        currentSection,
        sections,
        setChanges,
        setCurrentSection,
        setSections,
        setSettings,
        setTeams,
        teams,
      }}
    >
      {children}
    </SectionContext.Provider>
  );
};
